import { Controller } from "@hotwired/stimulus"

// Track-map orientation control (Better Corners — Slice 4).
//
// Drives the hero map's live rotation preview (track-map-hero#setRotation) and
// persists the saved orientation (PATCH …/orientation, map_rotation_deg) on Save.
//
// Editing affordances, all funnelling into preview(deg):
//   • presets 0/90/180/270        — preview that angle (Save commits)
//   • fine slider 0–359           — preview on input
//   • drag-to-rotate on the map   — angle delta from element centre while the
//                                   panel is open; snaps to nearest 5° on release
//
// Values:
//   rotation — current (live) rotation in degrees (seeded from map_rotation_deg)
//   base     — last-saved rotation in degrees (dirty = rotation !== base)
//   saveUrl  — PATCH endpoint
//
// Outlet:
//   track-map-hero (#track_hero_map) — exposes setRotation(deg) for live preview.
//
// The Orient chip + panel live as SIBLINGS of #track_hero_map inside the same
// wrapper, NOT as children — the hero controller wipes its own element's
// innerHTML on every render, which would otherwise delete this UI.
export default class extends Controller {
  static outlets = ["track-map-hero"]
  static targets = [
    "panel",
    "chip",
    "chipDegrees",
    "slider",
    "degreesInput",
    "preset",
    "saveButton",
    "resetButton",
  ]
  static values = {
    rotation: Number,
    base: Number,
    saveUrl: String,
  }

  connect() {
    this._open = false
    this._dragging = null
    // Bound handlers so add/removeEventListener pair up across drag sessions.
    this._onPointerMove = this._onPointerMove.bind(this)
    this._onPointerUp = this._onPointerUp.bind(this)
    this._syncUI()
  }

  disconnect() {
    this._teardownDrag()
    this._unbindMapDrag()
  }

  // ── Open / close ────────────────────────────────────────────────────────────

  toggle() {
    this._open ? this.close() : this.open()
  }

  open() {
    this._open = true
    this.panelTarget.classList.remove("hidden")
    if (this.hasChipTarget) this.chipTarget.classList.add("hidden")
    this._bindMapDrag()
    this._syncUI()
  }

  close() {
    this._open = false
    this.panelTarget.classList.add("hidden")
    if (this.hasChipTarget) this.chipTarget.classList.remove("hidden")
    this._teardownDrag()
    this._unbindMapDrag()
  }

  // ── Editing affordances ──────────────────────────────────────────────────────

  // Preset button → preview that angle. Save still persists.
  preset(event) {
    const deg = Number(event.currentTarget.dataset.deg)
    this.preview(deg)
  }

  // Fine slider → preview on input (integer, coarse).
  slide(event) {
    this.preview(Number(event.currentTarget.value))
  }

  // Numeric input → preview the exact typed value (fractional degrees allowed).
  // Fires on commit (blur / Enter); empty or non-numeric input is ignored.
  typeDegrees(event) {
    const v = parseFloat(event.currentTarget.value)
    if (Number.isFinite(v)) this.preview(v)
  }

  // Live preview (no persistence): normalize, store, drive the hero map.
  preview(deg) {
    this.rotationValue = this._normalize(deg)
    if (this.hasTrackMapHeroOutlet)
      this.trackMapHeroOutlet.setRotation(this.rotationValue)
    this._syncUI()
  }

  // ── Save / Reset ─────────────────────────────────────────────────────────────

  async save() {
    if (!this._dirty()) return
    await this._persist(this.rotationValue, { closeOnSuccess: true })
  }

  async reset() {
    if (this.rotationValue === 0 && this.baseValue === 0) return
    this.preview(0)
    await this._persist(0, { closeOnSuccess: false })
  }

  async _persist(deg, { closeOnSuccess }) {
    const value = this._normalize(deg)
    try {
      const res = await fetch(this.saveUrlValue, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": this._csrfToken(),
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: "map_rotation_deg=" + encodeURIComponent(value),
      })
      if (!res.ok) {
        // Leave the panel open so the user can retry; keep the live preview.
        return
      }
      this.baseValue = value
      this.rotationValue = value
      if (this.hasTrackMapHeroOutlet) this.trackMapHeroOutlet.setRotation(value)
      this._syncUI()
      if (closeOnSuccess) this.close()
    } catch {
      // Network error — leave panel open, preview intact, no crash.
    }
  }

  // ── Drag-to-rotate on the hero map ────────────────────────────────────────────
  // Active only while the panel is open. Angle delta from the element centre;
  // snaps to nearest 5° on release.

  _bindMapDrag() {
    const el = this._mapElement()
    if (!el) return
    el.style.cursor = "grab"
    this._dragStart = this._dragStart || this._onDragStart.bind(this)
    el.addEventListener("mousedown", this._dragStart)
    el.addEventListener("touchstart", this._dragStart, { passive: false })
  }

  _unbindMapDrag() {
    const el = this._mapElement()
    if (!el) return
    el.style.cursor = ""
    if (this._dragStart) {
      el.removeEventListener("mousedown", this._dragStart)
      el.removeEventListener("touchstart", this._dragStart)
    }
  }

  _onDragStart(event) {
    const el = this._mapElement()
    if (!el) return
    const r = el.getBoundingClientRect()
    const cx = r.left + r.width / 2
    const cy = r.top + r.height / 2
    const pt = event.touches ? event.touches[0] : event
    const a0 = (Math.atan2(pt.clientY - cy, pt.clientX - cx) * 180) / Math.PI
    this._dragging = { cx, cy, a0, r0: this.rotationValue }
    window.addEventListener("mousemove", this._onPointerMove)
    window.addEventListener("mouseup", this._onPointerUp)
    window.addEventListener("touchmove", this._onPointerMove, {
      passive: false,
    })
    window.addEventListener("touchend", this._onPointerUp)
  }

  _onPointerMove(event) {
    if (!this._dragging) return
    if (event.cancelable) event.preventDefault()
    const pt = event.touches ? event.touches[0] : event
    const a =
      (Math.atan2(
        pt.clientY - this._dragging.cy,
        pt.clientX - this._dragging.cx,
      ) *
        180) /
      Math.PI
    this.preview(this._dragging.r0 + (a - this._dragging.a0))
  }

  _onPointerUp() {
    this._teardownDrag()
    // Snap to nearest 5° on release (preview only — Save still persists).
    this.preview(this._snap5(this.rotationValue))
  }

  _teardownDrag() {
    window.removeEventListener("mousemove", this._onPointerMove)
    window.removeEventListener("mouseup", this._onPointerUp)
    window.removeEventListener("touchmove", this._onPointerMove)
    window.removeEventListener("touchend", this._onPointerUp)
    this._dragging = null
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────

  _mapElement() {
    return this.hasTrackMapHeroOutlet ? this.trackMapHeroOutlet.element : null
  }

  // Wrap into [0, 360) WITHOUT rounding — fractional degrees are preserved so the
  // numeric input can set precise values (the column + render pipeline are float).
  _normalize(deg) {
    const n = Number(deg)
    if (!Number.isFinite(n)) return this._normalize(this.rotationValue || 0)
    return ((n % 360) + 360) % 360
  }

  // Snap to nearest 5° — only the coarse map-drag uses this; the slider and the
  // numeric input set exact values.
  _snap5(deg) {
    return this._normalize(Math.round(this._normalize(deg) / 5) * 5)
  }

  // Display: at most one decimal place, trailing ".0" dropped (303 → "303",
  // 303.5 → "303.5").
  _fmt(deg) {
    const r = Math.round(this._normalize(deg) * 10) / 10
    return Number.isInteger(r) ? String(r) : r.toFixed(1)
  }

  _dirty() {
    return (
      Math.abs(
        this._normalize(this.rotationValue) - this._normalize(this.baseValue),
      ) > 1e-6
    )
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  // Reflect current rotation across the chip, slider, readout, presets, buttons.
  _syncUI() {
    const norm = this._normalize(this.rotationValue)

    // Slider is integer/coarse — reflect the rounded angle.
    if (
      this.hasSliderTarget &&
      Number(this.sliderTarget.value) !== Math.round(norm)
    ) {
      this.sliderTarget.value = Math.round(norm)
    }
    // Numeric input shows the exact (≤1 dp) value, but don't clobber it while the
    // user is typing into it.
    if (
      this.hasDegreesInputTarget &&
      document.activeElement !== this.degreesInputTarget
    ) {
      this.degreesInputTarget.value = this._fmt(norm)
    }

    // Chip degrees badge (shown only when non-zero).
    if (this.hasChipDegreesTarget) {
      if (norm !== 0) {
        this.chipDegreesTarget.textContent = `${this._fmt(norm)}°`
        this.chipDegreesTarget.classList.remove("hidden")
      } else {
        this.chipDegreesTarget.classList.add("hidden")
      }
    }

    // Active preset highlight.
    if (this.hasPresetTarget) {
      for (const btn of this.presetTargets) {
        const active = Number(btn.dataset.deg) === norm
        btn.classList.toggle("bg-vivid-purple", active)
        btn.classList.toggle("text-white", active)
        btn.classList.toggle("text-app-text-secondary", !active)
        btn.classList.toggle("hover:bg-app-surface-hover", !active)
      }
    }

    // Save: enabled only when dirty.
    if (this.hasSaveButtonTarget)
      this.saveButtonTarget.disabled = !this._dirty()
    // Reset: disabled when already at 0.
    if (this.hasResetButtonTarget) this.resetButtonTarget.disabled = norm === 0
  }
}
